<template>
  <v-dialog
    width="680px"
    persistent
    scrollable
    v-model="isOpened"
  >
    <send-by-email-dialog
      v-model="isSendByEmailDialogOpened"
      :report="report"
      v-if="reportId"
    />
    <image-dialog
      v-model="isImageDialogOpened"
      :url="openedImageUrl"
    />
    <v-card>
      <v-card-title>
        Compte-rendu d'intervention
        <v-spacer />
        <v-menu bottom auto offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="n-white-btn n-small-btn px-2 mr-4"
              v-on="on"
              v-bind="attrs"
              v-if="reportId"
            >
              Actions
              <v-icon small class="ml-2">arrow_drop_down</v-icon>
            </v-btn>
          </template>
          <v-list dense class="py-0">
            <v-list-item
              :href="`${apiBaseUrl}/intervention-reports/${reportId}/indeego-CR${reportId}.pdf`"
              target="_blank"
            >
              <v-list-item-avatar size="30">
                <v-icon>print</v-icon>
              </v-list-item-avatar>
              <v-list-item-content class="pr-4">
                Imprimer
              </v-list-item-content>
            </v-list-item>
            <v-list-item :disabled="!$canWrite" @click="$canWrite && (isSendByEmailDialogOpened = true)" v-if="canSendReportByEmail">
              <v-list-item-avatar size="30">
                <v-icon>send</v-icon>
              </v-list-item-avatar>
              <v-list-item-content class="pr-4">
                Envoyer par email
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn icon @click="isOpened = false">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <template v-if="reportId">
        <v-card-text class="pa-12" v-if="isFetchingReport">
          <v-row no-gutters justify="center" align="center">
            <v-progress-linear
              color="primary"
              indeterminate
              rounded
              height="6"
              style="max-width: 200px;"
            />
          </v-row>
        </v-card-text>
        <v-card-text class="px-6 pt-6 pb-10" v-else>
          <v-row no-gutters justify="center" align="start">
            <v-icon color="accent">outlined_flag</v-icon>
            <v-col class="ml-4">
              <div class="font-weight-medium text-subtitle-1 pb-1">
                Demande initiale
              </div>
              <n-card depressed v-html="report.$interventionHtmlDescription" class="px-4 py-3 primary--text" />
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="start" class="pt-6">
            <v-icon color="accent">business</v-icon>
            <v-col class="ml-4">
              <div class="font-weight-medium text-subtitle-1 pb-1">
                Site concerné
              </div>
              <div class="font-weight-bold text-subtitle-1 primary--text">
                {{ report.intervention.site.name }}
                <span v-if="report.$sitePostalCodeAndCity.length">&nbsp;- {{ report.$sitePostalCodeAndCity }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="center" class="pt-6">
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">label</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Statut après intervention
                  </div>
                  <div>
                    <v-chip
                      :color="report.status.color"
                      class="font-weight-medium text-center"
                      :style="{
                        color: report.status.fontColor,
                      }"
                    >
                      <div class="px-3">{{ report.status.name }}</div>
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">engineering</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Intervenant
                  </div>
                  <div class="font-weight-bold text-subtitle-1 primary--text">
                    {{ report.user.firstname }} {{ report.user.lastname }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="center" class="pt-6">
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">event</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Date d'intervention
                  </div>
                  <div class="font-weight-bold text-subtitle-1 primary--text">
                    {{ reportDate }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">hourglass_bottom</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Durée d'intervention
                  </div>
                  <div class="font-weight-bold text-subtitle-1 primary--text">
                    {{ report.duration | formatMinutesDuration }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="start" class="pt-6">
            <v-icon color="accent">rate_review</v-icon>
            <v-col class="ml-4">
              <div class="font-weight-medium text-subtitle-1 pb-1">
                Rapport d'intervention
              </div>
              <n-card depressed v-html="description" class="px-4 py-3 primary--text" />
            </v-col>
          </v-row>
          <v-row no-gutters justify="start" align="start" class="pt-8">
            <div>
              <v-icon color="accent">photo_camera</v-icon>
            </div>
            <v-col>
              <div class="font-weight-medium text-subtitle-1 ml-4">
                Photos
              </div>
              <v-row no-gutters justify="start" align="center" class="font-italic ml-4" v-if="!photos.length">
                Aucune photo ajoutée
              </v-row>
              <v-row no-gutters justify="start" align="stretch" class="ml-1">
                <div
                  v-for="(photo, i) in photos"
                  :key="photo.id"
                  class="px-2"
                >
                  <div class="fill-height pb-3" style="display: flex; flex-direction: column;">
                    <div class="text-subtitle-2 text-center primary--text pb-1">
                      Photo n°{{ i + 1 }}
                    </div>
                    <v-row no-gutters justify="center" align="center" style="flex: 1;">
                      <n-card
                        clickable
                        depressed
                        class="report-photo-thumbnail"
                        @click="openImage(photo.$url)"
                      >
                        <v-img
                          :src="photo.$url"
                          max-height="130"
                          max-width="100"
                        />
                      </n-card>
                    </v-row>
                  </div>
                </div>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="start" class="pt-3">
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">gesture</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Interlocuteur client
                  </div>
                  <div class="font-weight-bold text-subtitle-1 primary--text" v-if="report.interlocutorName">
                    {{ report.interlocutorName }}
                  </div>
                  <div class="font-italic text-subtitle-1 primary--text" v-else>
                    Non renseigné
                  </div>
                  <n-card depressed class="pa-4 mt-2" v-if="interlocutorSignatureUrl">
                    <v-row no-gutters justify="center" align="center" style="height: 150px;">
                      <img
                        :src="interlocutorSignatureUrl"
                        style="max-height: 100%; width: auto; max-width: 200px;"
                        hheight="100%"
                        mmax-width="130"
                        wwidth="50%"
                        contain
                      />
                    </v-row>
                  </n-card>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters justify="center" align="start">
                <div class="pr-4">
                  <v-icon color="accent">gesture</v-icon>
                </div>
                <v-col>
                  <div class="font-weight-medium text-subtitle-1">
                    Technicien
                  </div>
                  <div class="font-weight-bold text-subtitle-1 primary--text">
                    {{ report.user.firstname }} {{ report.user.lastname }}
                  </div>
                  <n-card depressed class="pa-4 mt-2" v-if="userSignatureUrl">
                    <v-row no-gutters justify="center" align="center" style="height: 150px;">
                      <img
                        :src="userSignatureUrl"
                        style="max-height: 100%; width: auto; max-width: 200px;"
                        hheight="100%"
                        mmax-width="130"
                        wwidth="50%"
                        contain
                      />
                    </v-row>
                  </n-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import moment from 'moment';
import SendByEmailDialog from '@/components/Interventions/Report/SendByEmailDialog.vue';
import ImageDialog from '@/components/Interventions/Report/ImageDialog.vue';

const PHOTO_BASE_URL = `https://files.nestorr.io`;

export default {
  props: {
    value: Boolean,
    reportId: {
      type: Number,
      required: false,
    },
  },
  data: () => ({
    isFetchingReport: false,
    isSendByEmailDialogOpened: false,
    isImageDialogOpened: false,
    openedImageUrl: null,
  }),
  computed: {
    ...mapState(`ui/mainLayout`, [
      `apiBaseUrl`,
    ]),
    ...mapGetters(`ui/interventionReport`, [
      `report`,
    ]),
    ...mapState(`model/user`, [
      `isMakerUser`,
      `isSelfMakerUser`,
    ]),
    isOpened: {
      get() { return this.value; },
      set(value) { this.$emit(`input`, value); },
    },
    hasReport() {
      return Boolean(this.report?.id);
    },
    reportDate() {
      return moment(this.report.reportDate).format(`[Le] DD/MM/YYYY [à] HH[h]mm`);
    },
    description() {
      return this.report.description.replace(/\r\n|\r|\n/g, `<br />`);
    },
    photos() {
      return this.report.photos.map(photo => ({
        ...photo,
        $url: `${PHOTO_BASE_URL}/report-photos/${photo.Report_id}/${photo.uid}`,
      }));
    },
    interlocutorSignatureUrl() {
      if (this.report.interlocutorSignatureUid) {
        return `${PHOTO_BASE_URL}/report-signatures/${this.report.id}/${this.report.interlocutorSignatureUid}`;
      }
      return null;
    },
    userSignatureUrl() {
      if (this.report.userSignatureUid) {
        return `${PHOTO_BASE_URL}/report-signatures/${this.report.id}/${this.report.userSignatureUid}`;
      }
      return null;
    },
    canSendReportByEmail() {
      return this.isMakerUser || this.isSelfMakerUser;
    },
  },
  methods: {
    ...mapMutations(`ui/interventionReport`, {
      setReportId: `SET_REPORT_ID`,
    }),
    ...mapActions(`ui/interventionReport`, {
      storeFetchReport: `fetchReport`,
    }),
    async fetchReport() {
      this.isFetchingReport = true;
      await this.storeFetchReport();
      this.isFetchingReport = false;
    },
    openImage(url) {
      this.openedImageUrl = url;
      this.isImageDialogOpened = true;
    },
  },
  beforeMount() {
    this.setReportId(this.reportId);
    this.fetchReport();
  },
  components: {
    SendByEmailDialog,
    ImageDialog,
  },
};
</script>

<style lang="scss" scoped>
</style>
